import Decimal from 'decimal.js';

/**
 * P21 PR 4 – Cổng nhân viên (ESS): xin phép, tạm ứng, hợp đồng lao động, nội quy (docs/phases/P21.md §9.6, DEC-194…199).
 * Hàm thuần dùng chung API (nguồn sự thật) và web (xem trước). Số ngày nghỉ theo bước 0,5; tiền VND làm tròn ROUND_HALF_UP.
 */

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

// ---------------------------------------------------------------------------
// Tham số mặc định (lưu ở bảng ess_settings, sửa trên giao diện; số dưới đây là mặc định của migration 0086)
// ---------------------------------------------------------------------------
export interface EssSettings {
  /** Trần tạm ứng / tháng = tỷ lệ × lương hợp đồng (0,5 = 50%) */
  advanceMaxRatio: number;
  /** Phép năm cơ bản / năm */
  annualLeaveDays: number;
  /** Cứ đủ X năm thâm niên … */
  seniorityStepYears: number;
  /** … thì thêm Y ngày phép */
  seniorityBonusDays: number;
  /** Nhắc hợp đồng sắp hết hạn trước N ngày */
  contractWarnDays: number;
  /** Phép năm theo công tháng (DEC-244): tháng đủ từ N công trở lên … */
  leaveAccrualMinWork: number;
  /** … thì NV chính thức được cộng M ngày phép */
  leaveAccrualDays: number;
  /** DEC-811: tháng nghỉ từ N ngày trở lên (có phép + không phép) thì mất phép năm của tháng đó; 0 = tắt */
  leaveForfeitAbsentDays: number;
  /** DEC-811: tháng vắng không phép từ N ngày trở lên thì mất phép năm của tháng đó; 0 = tắt */
  leaveForfeitUnexcusedDays: number;
  /** Đơn nghỉ phải gửi trước giờ vào ca ít nhất N phút (DEC-246) */
  leaveMinLeadMinutes: number;
  /** Đơn gửi trước giờ vào ca dưới N giờ phải kèm ảnh xác thực */
  leaveEvidenceHours: number;
}
export const DEFAULT_ESS_SETTINGS: EssSettings = {
  advanceMaxRatio: 0.5,
  annualLeaveDays: 12,
  seniorityStepYears: 5,
  seniorityBonusDays: 1,
  contractWarnDays: 30,
  leaveAccrualMinWork: 20,
  leaveAccrualDays: 1,
  leaveForfeitAbsentDays: 3,
  leaveForfeitUnexcusedDays: 2,
  leaveMinLeadMinutes: 120,
  leaveEvidenceHours: 24,
};

// ---------------------------------------------------------------------------
// Nghỉ phép
// ---------------------------------------------------------------------------
export const LEAVE_PARTS = ['full', 'am', 'pm'] as const;
export type LeavePart = (typeof LEAVE_PARTS)[number];
export const LEAVE_PART_LABELS: Record<LeavePart, string> = { full: 'Cả ngày', am: 'Buổi sáng', pm: 'Buổi chiều' };

export const LEAVE_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

export const ADVANCE_STATUSES = ['pending_check', 'pending_approval', 'approved', 'rejected', 'returned', 'cancelled'] as const;
export type AdvanceStatus = (typeof ADVANCE_STATUSES)[number];
export const ADVANCE_STATUS_LABELS: Record<AdvanceStatus, string> = {
  pending_check: 'Chờ kế toán kiểm',
  pending_approval: 'Chờ GĐ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
  returned: 'Trả lại',
  cancelled: 'Đã hủy',
};
/** Đề nghị tạm ứng còn "giữ chỗ" trong hạn mức tháng (chưa hủy, chưa bị từ chối) */
export const ADVANCE_OPEN_STATUSES: readonly AdvanceStatus[] = ['pending_check', 'pending_approval', 'approved', 'returned'];

export const ADVANCE_METHODS = ['transfer', 'cash'] as const;
export type AdvanceMethod = (typeof ADVANCE_METHODS)[number];
export const ADVANCE_METHOD_LABELS: Record<AdvanceMethod, string> = { transfer: 'Chuyển khoản', cash: 'Tiền mặt' };

export const weekdayOfDate = (date: string) => new Date(`${date}T00:00:00Z`).getUTCDay();

function nextDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export interface LeaveRange {
  fromDate: string;
  toDate: string;
  /** Ngày đầu: 'full' hoặc 'pm' (nghỉ từ chiều). Nghỉ 1 ngày: 'full' | 'am' | 'pm'. */
  fromPart: LeavePart;
  /** Ngày cuối: 'full' hoặc 'am' (nghỉ hết buổi sáng). Nghỉ 1 ngày: bằng fromPart. */
  toPart: LeavePart;
}

/** Kiểm khoảng nghỉ; trả thông báo lỗi hoặc null. */
export function validateLeaveRange(r: LeaveRange): string | null {
  if (r.toDate < r.fromDate) return 'Ngày kết thúc phải sau hoặc bằng ngày bắt đầu';
  if (r.fromDate.slice(0, 4) !== r.toDate.slice(0, 4)) return 'Đơn nghỉ không được qua 2 năm, tách thành 2 đơn';
  if (r.fromDate === r.toDate) {
    if (r.fromPart !== r.toPart) return 'Nghỉ trong 1 ngày: chọn cả ngày, buổi sáng hoặc buổi chiều';
    return null;
  }
  if (r.fromPart === 'am') return 'Ngày bắt đầu chỉ chọn cả ngày hoặc nghỉ từ buổi chiều';
  if (r.toPart === 'pm') return 'Ngày kết thúc chỉ chọn cả ngày hoặc nghỉ hết buổi sáng';
  return null;
}

/**
 * Từng ngày nghỉ (bỏ ngày nghỉ hằng tuần, mặc định Chủ nhật – DEC-176) với phần ngày: 1 = cả ngày, 0,5 = nửa ngày.
 */
export function leaveDayMap(r: LeaveRange, weeklyOff: readonly number[] = [0]): { date: string; part: LeavePart; fraction: number }[] {
  const out: { date: string; part: LeavePart; fraction: number }[] = [];
  if (validateLeaveRange(r)) return out;
  for (let d = r.fromDate; d <= r.toDate; d = nextDay(d)) {
    if (weeklyOff.includes(weekdayOfDate(d))) continue;
    const part: LeavePart = d === r.fromDate ? r.fromPart : d === r.toDate ? r.toPart : 'full';
    out.push({ date: d, part, fraction: part === 'full' ? 1 : 0.5 });
  }
  return out;
}

/** Tổng số ngày nghỉ của đơn (bước 0,5). */
export function leaveDays(r: LeaveRange, weeklyOff: readonly number[] = [0]): number {
  return leaveDayMap(r, weeklyOff).reduce((s, x) => s + x.fraction, 0);
}

/**
 * DEC-247: đơn phép năm dài hơn `max` ngày → tách: `max` ngày đầu là phép năm, phần còn lại là nghỉ không lương.
 * Trả null khi không cần tách hoặc không tách tròn được (đơn bắt đầu buổi chiều làm số ngày lệch 0,5).
 */
export function splitAnnualLeave(r: LeaveRange, max: number, weeklyOff: readonly number[] = [0]): { annual: LeaveRange; extra: LeaveRange } | null {
  const days = leaveDayMap(r, weeklyOff);
  let cum = 0;
  for (let k = 0; k < days.length - 1; k++) {
    cum += days[k]!.fraction;
    if (cum < max) continue;
    if (cum > max) return null;
    const cut = days[k]!;
    const next = days[k + 1]!;
    const annual: LeaveRange = { fromDate: r.fromDate, fromPart: r.fromPart, toDate: cut.date, toPart: cut.date === r.fromDate ? r.fromPart : 'full' };
    const extra: LeaveRange = { fromDate: next.date, fromPart: next.date === r.toDate ? r.toPart : 'full', toDate: r.toDate, toPart: r.toPart };
    return { annual, extra };
  }
  return null;
}

/** Hai đơn nghỉ có trùng buổi không (cùng ngày: cả ngày đụng mọi buổi; sáng và chiều không trùng nhau). */
export function leaveOverlaps(a: LeaveRange, b: LeaveRange, weeklyOff: readonly number[] = [0]): boolean {
  const mb = new Map(leaveDayMap(b, weeklyOff).map((x) => [x.date, x.part]));
  for (const x of leaveDayMap(a, weeklyOff)) {
    const p = mb.get(x.date);
    if (!p) continue;
    if (p === 'full' || x.part === 'full' || p === x.part) return true;
  }
  return false;
}

/** Số năm thâm niên tròn tính tới ngày `asOf`. */
export function serviceYears(hiredAt: string | null, asOf: string): number {
  if (!hiredAt || hiredAt > asOf) return 0;
  let y = Number(asOf.slice(0, 4)) - Number(hiredAt.slice(0, 4));
  if (asOf.slice(5) < hiredAt.slice(5)) y -= 1;
  return Math.max(0, y);
}

const round1 = (v: Decimal.Value) => new D(v).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber();

export interface AnnualLeaveInput {
  hiredAt: string | null;
  year: number;
  /** Ngày xét (cộng dần tới hết tháng của ngày này); mặc định 31/12 của năm */
  asOf?: string;
  settings?: Pick<EssSettings, 'annualLeaveDays' | 'seniorityStepYears' | 'seniorityBonusDays'>;
}

export interface AnnualLeaveResult {
  /** Mức phép cả năm (cơ bản + thâm niên) */
  yearlyRate: number;
  /** Số tháng được cộng trong năm (từ tháng vào làm hoặc tháng 1 tới tháng của ngày xét) */
  months: number;
  /** Số được cấp tới ngày xét = mức năm × tháng / 12, làm tròn 1 chữ số lẻ */
  accrued: number;
  /** Số được cấp nếu làm hết năm */
  entitledYear: number;
  seniorityYears: number;
}

/**
 * Phép năm: mức năm = phép cơ bản (12) + thêm 1 ngày mỗi đủ 5 năm thâm niên (tính tới 01/01 của năm); cộng dần 1/12 mỗi tháng,
 * tháng đang chạy được cộng ngay đầu tháng; vào làm trong năm thì cộng từ tháng vào làm (DEC-195).
 */
export function annualLeave(i: AnnualLeaveInput): AnnualLeaveResult {
  const s = i.settings ?? DEFAULT_ESS_SETTINGS;
  const y = i.year;
  const jan1 = `${y}-01-01`;
  const seniority = serviceYears(i.hiredAt, jan1);
  const step = Math.max(1, s.seniorityStepYears);
  const yearlyRate = new D(s.annualLeaveDays).plus(new D(s.seniorityBonusDays).mul(Math.floor(seniority / step))).toNumber();
  const startMonth = i.hiredAt && i.hiredAt > jan1 ? (i.hiredAt.slice(0, 4) === String(y) ? Number(i.hiredAt.slice(5, 7)) : 13) : 1;
  const asOf = i.asOf ?? `${y}-12-31`;
  const asOfMonth = asOf < jan1 ? 0 : asOf.slice(0, 4) > String(y) ? 12 : Number(asOf.slice(5, 7));
  const months = Math.max(0, asOfMonth - startMonth + 1);
  const yearMonths = Math.max(0, 12 - startMonth + 1);
  return {
    yearlyRate,
    months,
    accrued: round1(new D(yearlyRate).mul(months).div(12)),
    entitledYear: round1(new D(yearlyRate).mul(yearMonths).div(12)),
    seniorityYears: seniority,
  };
}

export interface LeaveBalance {
  year: number;
  accrued: number;
  /** Ghi đè số được cấp cả năm (nhập tay, có lý do); null = theo công thức */
  grantedOverride: number | null;
  adjust: number;
  used: number;
  pending: number;
  /** Còn được nghỉ = cấp tới nay + điều chỉnh − đã dùng − đang chờ duyệt */
  available: number;
}

export function leaveBalance(x: { year: number; accrued: number; grantedOverride: number | null; adjust: number; used: number; pending: number }): LeaveBalance {
  const base = x.grantedOverride ?? x.accrued;
  return { ...x, available: round1(new D(base).plus(x.adjust).minus(x.used).minus(x.pending)) };
}

// ---------------------------------------------------------------------------
// Phép năm theo công tháng (DEC-244, như Minhtien-CRM)
// ---------------------------------------------------------------------------
/** Các tháng 'YYYY-MM' từ `from` tới `to` (gồm cả hai đầu). */
export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  let [y, m] = from.slice(0, 7).split('-').map(Number) as [number, number];
  const end = to.slice(0, 7);
  for (let i = 0; i < 1200; i += 1) {
    const k = `${y}-${String(m).padStart(2, '0')}`;
    if (k > end) break;
    out.push(k);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export interface LeaveLedgerMonth {
  month: string;
  /** NV chính thức trong tháng này (được cộng phép) */
  eligible: boolean;
  /** Công tháng (gồm ngày nghỉ có lương) */
  work: number;
  /** Phép năm đã nghỉ (đơn đã duyệt bắt đầu trong tháng) */
  used: number;
  /** Số ngày vắng không phép trong tháng */
  unexcused: number;
  /** DEC-811: số ngày nghỉ có đơn đã duyệt trong tháng (mọi loại, trừ "Công ty cho nghỉ" và lễ; nửa ngày = 0,5) */
  absent?: number;
}

export interface LeaveLedgerRow extends LeaveLedgerMonth {
  accrued: number;
  /** Ngày phép bị trừ vì vắng không phép. DEC-247 (theo Minhtien-CRM): luôn 0 – vắng không phép mất công + mất chuyên cần, không trừ phép */
  deducted: number;
  /** Phép năm trước còn thừa → hủy đầu tháng 1 (DEC-247 theo Minhtien-CRM: không chuyển năm, thay DEC-178) */
  expired: number;
  /** Số dư cuối tháng */
  closing: number;
  /** DEC-811: đủ công nhưng mất phép tháng này: 'absent' = nghỉ ≥ N ngày, 'unexcused' = vắng không phép ≥ N ngày */
  forfeit: LeaveForfeitReason | null;
}

export type LeaveForfeitReason = 'absent' | 'unexcused';
export const LEAVE_FORFEIT_LABELS: Record<LeaveForfeitReason, string> = {
  absent: 'Mất phép: nghỉ nhiều ngày trong tháng',
  unexcused: 'Mất phép: vắng không phép',
};

/**
 * DEC-811 (anh Tiến 03/11/2026): tháng nào nghỉ từ `leaveForfeitAbsentDays` ngày (có phép + không phép, mặc định 3)
 * hoặc vắng không phép từ `leaveForfeitUnexcusedDays` ngày (mặc định 2) thì không được cộng phép tháng đó, dù đủ công.
 * Tham số 0 = tắt điều kiện đó.
 */
export function leaveForfeit(
  m: Pick<LeaveLedgerMonth, 'unexcused' | 'absent'>,
  s: Partial<Pick<EssSettings, 'leaveForfeitAbsentDays' | 'leaveForfeitUnexcusedDays'>>,
): LeaveForfeitReason | null {
  const kp = m.unexcused ?? 0;
  const total = kp + (m.absent ?? 0);
  const nUnexcused = s.leaveForfeitUnexcusedDays ?? 0;
  const nAbsent = s.leaveForfeitAbsentDays ?? 0;
  if (nUnexcused > 0 && kp >= nUnexcused) return 'unexcused';
  if (nAbsent > 0 && total >= nAbsent) return 'absent';
  return null;
}

/** Nghỉ phép năm tối đa N ngày / đơn; phần vượt thành nghỉ không lương (DEC-247, như Minhtien-CRM) */
export const LEAVE_MAX_DAYS_PER_REQUEST = 3;

/**
 * Sổ phép chạy theo tháng: đầu kỳ + cộng (NV chính thức, công tháng ≥ ngưỡng → cộng M ngày; tháng đang chạy cộng ngay khi đủ công)
 * − phép đã nghỉ. DEC-811: tháng nghỉ ≥ 3 ngày (có phép + không phép) hoặc vắng không phép ≥ 2 ngày thì không cộng phép tháng đó.
 * DEC-247 (anh Tiến 11/10, như Minhtien-CRM): vắng không phép KHÔNG trừ phép (đã mất công + chuyên cần);
 * phép không chuyển sang năm sau: số dư cuối năm hủy đầu tháng 1.
 */
export function leaveLedger(
  opening: number,
  months: LeaveLedgerMonth[],
  s: Pick<EssSettings, 'leaveAccrualMinWork' | 'leaveAccrualDays'> & Partial<Pick<EssSettings, 'leaveForfeitAbsentDays' | 'leaveForfeitUnexcusedDays'>>,
) {
  let running = new D(opening);
  let accrued = new D(0);
  let used = new D(0);
  let expired = new D(0);
  const rows: LeaveLedgerRow[] = months.map((m, i) => {
    const mm = Number(m.month.slice(5, 7));
    let exp = new D(0);
    if (mm === 1 && i > 0 && running.gt(0)) {
      exp = running;
      running = new D(0);
    }
    const enough = m.eligible && m.work >= s.leaveAccrualMinWork;
    const forfeit = enough ? leaveForfeit(m, s) : null;
    const acc = enough && !forfeit ? s.leaveAccrualDays : 0;
    running = running.plus(acc).minus(m.used);
    accrued = accrued.plus(acc);
    used = used.plus(m.used);
    expired = expired.plus(exp);
    return { ...m, accrued: acc, deducted: 0, expired: round1(exp), closing: round1(running), forfeit };
  });
  return { rows, accrued: round1(accrued), used: round1(used), deducted: 0, expired: round1(expired), closing: round1(running) };
}

// ---------------------------------------------------------------------------
// Quy định gửi đơn nghỉ, xin đi trễ (DEC-246)
// ---------------------------------------------------------------------------
/**
 * Đơn nghỉ: giờ bắt đầu nghỉ = giờ vào ca (buổi chiều: giờ vào ca chiều) ngày bắt đầu, giờ VN.
 * Phải gửi trước ít nhất `minLeadMinutes`; gửi trước dưới `evidenceHours` giờ thì phải kèm ảnh xác thực.
 */
export function leaveTiming(i: { fromDate: string; startTime: string; now: Date; minLeadMinutes: number; evidenceHours: number }) {
  const start = new Date(`${i.fromDate}T${i.startTime.slice(0, 5)}:00+07:00`);
  const minutesBefore = Math.floor((start.getTime() - i.now.getTime()) / 60_000);
  return {
    startAt: start.toISOString(),
    minutesBefore,
    tooLate: minutesBefore < i.minLeadMinutes,
    evidenceByTime: minutesBefore < i.evidenceHours * 60,
  };
}

/**
 * Đơn xác thực giờ làm (DEC-246): đi trễ (xe hư…), về nửa chừng, được điều đi nơi khác. Đều phải có ảnh chụp tại chỗ + vị trí GPS,
 * Trưởng phòng / GĐ duyệt. Đi trễ / về nửa chừng: phút trễ / về sớm VẪN tính, duyệt = hợp lý (không lỗi chuyên cần); đã gửi đơn đi trễ thì chấm vào được sau giờ đóng cổng;
 * điều đi → ngày làm việc đó tính đủ 1 công, không tính trễ / về sớm.
 */
export const EXCUSE_KINDS = ['late', 'early', 'dispatch'] as const;
export type AttendanceExcuseKind = (typeof EXCUSE_KINDS)[number];
export const EXCUSE_KIND_LABELS: Record<AttendanceExcuseKind, string> = { late: 'Đi trễ', early: 'Về nửa chừng', dispatch: 'Điều đi nơi khác' };
/** Giờ ghi kèm đơn: đi trễ = dự kiến tới, về nửa chừng = giờ về, điều đi = giờ đi. */
export const EXCUSE_TIME_LABELS: Record<AttendanceExcuseKind, string> = { late: 'Dự kiến tới lúc', early: 'Về lúc', dispatch: 'Đi lúc' };
export const EXCUSE_REASONS = ['vehicle_breakdown', 'sick', 'family', 'customer_site', 'construction', 'other'] as const;
export type AttendanceExcuseReason = (typeof EXCUSE_REASONS)[number];
export const EXCUSE_REASON_LABELS: Record<AttendanceExcuseReason, string> = {
  vehicle_breakdown: 'Xe hư dọc đường',
  sick: 'Ốm, tai nạn',
  family: 'Việc gia đình gấp',
  customer_site: 'Đi gặp khách / giao hàng',
  construction: 'Đi công trình',
  other: 'Lý do khác',
};
/** Lý do hợp lệ theo loại đơn (ô chọn trên Cổng NV, server kiểm lại). */
export const EXCUSE_REASONS_BY_KIND: Record<AttendanceExcuseKind, readonly AttendanceExcuseReason[]> = {
  late: ['vehicle_breakdown', 'other'],
  early: ['sick', 'family', 'other'],
  dispatch: ['customer_site', 'construction', 'other'],
};

// ---------------------------------------------------------------------------
// Tạm ứng
// ---------------------------------------------------------------------------
/** Hạn mức tạm ứng / tháng = lương HĐ × tỷ lệ, làm tròn tới đồng (ROUND_HALF_UP). */
export function advanceLimit(contractSalary: number, ratio: number): number {
  return new D(contractSalary).mul(ratio).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

/** Còn được tạm ứng = hạn mức − tổng các đề nghị còn hiệu lực trong tháng (không âm). */
export function advanceRemaining(limit: number, used: number): number {
  return Decimal.max(0, new D(limit).minus(used)).toNumber();
}

/** Kiểm số tiền đề nghị; trả lỗi hoặc null. */
export function advanceError(amount: number, limit: number, used: number): string | null {
  if (!(amount > 0)) return 'Số tiền phải lớn hơn 0';
  const rest = advanceRemaining(limit, used);
  if (new D(amount).gt(rest)) return `Vượt hạn mức tạm ứng tháng: còn được ${rest.toLocaleString('vi-VN')} đ (hạn mức ${limit.toLocaleString('vi-VN')} đ)`;
  return null;
}

// ---------------------------------------------------------------------------
// Hợp đồng lao động
// ---------------------------------------------------------------------------
export const EMPLOYMENT_CONTRACT_TYPES = ['probation', 'fixed_term', 'indefinite', 'seasonal', 'piecework', 'appendix'] as const;
export type EmploymentContractType = (typeof EMPLOYMENT_CONTRACT_TYPES)[number];
export const EMPLOYMENT_CONTRACT_TYPE_LABELS: Record<EmploymentContractType, string> = {
  probation: 'Thử việc',
  fixed_term: 'Xác định thời hạn',
  indefinite: 'Không xác định thời hạn',
  seasonal: 'Thời vụ',
  piecework: 'Khoán việc',
  appendix: 'Phụ lục',
};

export const EMPLOYMENT_CONTRACT_STATUSES = ['active', 'terminated', 'cancelled'] as const;
export type EmploymentContractStatus = (typeof EMPLOYMENT_CONTRACT_STATUSES)[number];

/**
 * DEC-619 – HĐLĐ điện tử theo NĐ 337/2025/NĐ-CP + TT 08/2026/TT-BNV. Cách ký từng HĐ:
 * giấy; ký nội bộ trên ERP (DEC-301, KHÔNG phải HĐLĐ điện tử); eContract = ký số qua nhà cung cấp được Bộ Nội vụ công bố, có mã định danh.
 */
export const CONTRACT_SIGN_METHODS = ['paper', 'app', 'econtract'] as const;
export type ContractSignMethod = (typeof CONTRACT_SIGN_METHODS)[number];
export const CONTRACT_SIGN_METHOD_LABELS: Record<ContractSignMethod, string> = {
  paper: 'Ký giấy',
  app: 'Ký nội bộ trên ERP',
  econtract: 'HĐLĐ điện tử (eContract)',
};
/** Mã định danh HĐLĐ điện tử (TT 08/2026): A = giao kết từ 01/7/2026, B = chuyển từ giấy, C = giao kết trước 01/7/2026; 2 số đầu = năm cấp */
export const ECONTRACT_ID_RE = /^[ABC][0-9]{12}$/;
export const normalizeEcontractId = (v: string) => v.replace(/[\s.-]/g, '').toUpperCase();

/** Tình trạng ký hiển thị (tính lúc đọc) */
export const CONTRACT_SIGN_STATES = ['draft', 'company_pending', 'employee_pending', 'internal', 'paper', 'awaiting_id', 'done'] as const;
export type ContractSignState = (typeof CONTRACT_SIGN_STATES)[number];
export const CONTRACT_SIGN_STATE_LABELS: Record<ContractSignState, string> = {
  draft: 'Nháp',
  company_pending: 'Chờ Giám đốc ký',
  employee_pending: 'Chờ nhân viên ký',
  internal: 'Đã ký nội bộ (chưa phải HĐ điện tử)',
  paper: 'Đã ký giấy',
  awaiting_id: 'Đã ký eContract, chờ mã định danh',
  done: 'Hoàn tất',
};
export function contractSignState(c: {
  signMethod: ContractSignMethod;
  econtractId: string | null;
  isAppendix: boolean;
  fileId: number | null;
  signingStatus: string | null;
}): ContractSignState {
  if (c.signMethod === 'econtract') return c.econtractId || (c.isAppendix && c.fileId) ? 'done' : 'awaiting_id';
  if (c.signingStatus === 'company_pending' || c.signingStatus === 'employee_pending') return c.signingStatus;
  if (c.signingStatus === 'signed') return 'internal';
  if (c.fileId) return 'paper';
  return 'draft';
}

/** Trạng thái hiển thị (tính lúc đọc) */
export const CONTRACT_STATES = ['upcoming', 'active', 'expiring', 'expired', 'terminated', 'cancelled'] as const;
export type ContractState = (typeof CONTRACT_STATES)[number];
export const CONTRACT_STATE_LABELS: Record<ContractState, string> = {
  upcoming: 'Chưa hiệu lực',
  active: 'Đang hiệu lực',
  expiring: 'Sắp hết hạn',
  expired: 'Hết hạn',
  terminated: 'Đã chấm dứt',
  cancelled: 'Đã hủy',
};

const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

export function contractState(
  c: { status: EmploymentContractStatus; effectiveFrom: string; expiresOn: string | null },
  today: string,
  warnDays = DEFAULT_ESS_SETTINGS.contractWarnDays,
): { state: ContractState; daysLeft: number | null } {
  const daysLeft = c.expiresOn ? dayDiff(today, c.expiresOn) : null;
  if (c.status === 'cancelled') return { state: 'cancelled', daysLeft };
  if (c.status === 'terminated') return { state: 'terminated', daysLeft };
  if (c.effectiveFrom > today) return { state: 'upcoming', daysLeft };
  if (daysLeft !== null && daysLeft < 0) return { state: 'expired', daysLeft };
  if (daysLeft !== null && daysLeft <= warnDays) return { state: 'expiring', daysLeft };
  return { state: 'active', daysLeft };
}

// ---------------------------------------------------------------------------
// Hợp đồng thử việc (BLLĐ 2019 Điều 24–27; DEC-242)
// ---------------------------------------------------------------------------
/** Loại công việc quyết định thời gian thử việc tối đa (BLLĐ 2019 Điều 25) */
export const PROBATION_LEVELS = ['manager', 'college', 'intermediate', 'other'] as const;
export type ProbationLevel = (typeof PROBATION_LEVELS)[number];
export const PROBATION_LEVEL_LABELS: Record<ProbationLevel, string> = {
  manager: 'Quản lý doanh nghiệp (tối đa 180 ngày)',
  college: 'Cần trình độ cao đẳng trở lên (tối đa 60 ngày)',
  intermediate: 'Trung cấp, công nhân kỹ thuật, nhân viên nghiệp vụ (tối đa 30 ngày)',
  other: 'Công việc khác (tối đa 6 ngày làm việc)',
};
/** Số ngày tối đa; 'other' tính ngày làm việc (bỏ Chủ nhật), còn lại tính ngày lịch */
export const PROBATION_MAX_DAYS: Record<ProbationLevel, number> = { manager: 180, college: 60, intermediate: 30, other: 6 };
/** Lương thử việc tối thiểu = 85% lương của công việc (BLLĐ 2019 Điều 26) */
export const PROBATION_MIN_SALARY_RATE = 0.85;

/** Số ngày thử việc từ ngày bắt đầu đến hết ngày kết thúc (tính cả 2 đầu); workdaysOnly = bỏ Chủ nhật */
export function probationDays(from: string, to: string, workdaysOnly = false): number {
  const total = dayDiff(from, to) + 1;
  if (total <= 0) return 0;
  if (!workdaysOnly) return total;
  let n = 0;
  const start = Date.parse(`${from}T00:00:00Z`);
  for (let i = 0; i < total; i += 1) if (new Date(start + i * 86_400_000).getUTCDay() !== 0) n += 1;
  return n;
}

/** Tỷ lệ lương thử việc / lương công việc, % làm tròn 1 số lẻ; thiếu số thì null */
export function probationSalaryRate(salary: number | null | undefined, referenceSalary: number | null | undefined): number | null {
  if (salary === null || salary === undefined || !referenceSalary) return null;
  return Math.round((salary / referenceSalary) * 1000) / 10;
}

/** Kiểm thời hạn + lương thử việc theo luật; trả lỗi đầu tiên hoặc null */
export function probationError(i: {
  level: ProbationLevel | null | undefined;
  effectiveFrom: string;
  expiresOn: string | null | undefined;
  salary?: number | null;
  referenceSalary?: number | null;
}): { field: string; message: string } | null {
  if (!i.level) return { field: 'probationLevel', message: 'Chọn loại công việc thử việc (quyết định thời gian thử việc tối đa)' };
  if (!i.expiresOn) return { field: 'expiresOn', message: 'Nhập ngày kết thúc thử việc' };
  const max = PROBATION_MAX_DAYS[i.level];
  const days = probationDays(i.effectiveFrom, i.expiresOn, i.level === 'other');
  if (days > max) {
    return { field: 'expiresOn', message: `Thử việc ${days} ${i.level === 'other' ? 'ngày làm việc' : 'ngày'}, vượt mức tối đa ${max} ${i.level === 'other' ? 'ngày làm việc' : 'ngày'} của loại công việc này` };
  }
  if (i.salary !== null && i.salary !== undefined && i.referenceSalary) {
    const min = Math.ceil(i.referenceSalary * PROBATION_MIN_SALARY_RATE);
    if (i.salary < min) return { field: 'salary', message: `Lương thử việc phải ít nhất 85% lương công việc (tối thiểu ${min.toLocaleString('vi-VN')} đ)` };
  }
  return null;
}

export const PROBATION_RESULTS = ['passed', 'failed'] as const;
export type ProbationResult = (typeof PROBATION_RESULTS)[number];
export const PROBATION_RESULT_LABELS: Record<ProbationResult, string> = { passed: 'Đạt', failed: 'Không đạt' };

/** Trạng thái hiển thị của HĐ thử việc (tính lúc đọc) */
export const PROBATION_STATES = ['upcoming', 'running', 'ending', 'awaiting', 'passed', 'hired', 'failed', 'terminated', 'cancelled'] as const;
export type ProbationState = (typeof PROBATION_STATES)[number];
export const PROBATION_STATE_LABELS: Record<ProbationState, string> = {
  upcoming: 'Chưa bắt đầu',
  running: 'Đang thử việc',
  ending: 'Sắp hết thử việc',
  awaiting: 'Chờ đánh giá',
  passed: 'Đạt – chưa ký HĐLĐ',
  hired: 'Đạt – đã ký HĐLĐ',
  failed: 'Không đạt',
  terminated: 'Đã chấm dứt',
  cancelled: 'Đã hủy',
};
/** Nhắc đánh giá trước khi hết thử việc N ngày */
export const PROBATION_WARN_DAYS = 7;

export function probationState(
  c: { status: EmploymentContractStatus; effectiveFrom: string; expiresOn: string | null; result: ProbationResult | null; hasNextContract: boolean },
  today: string,
  warnDays = PROBATION_WARN_DAYS,
): { state: ProbationState; daysLeft: number | null } {
  const daysLeft = c.expiresOn ? dayDiff(today, c.expiresOn) : null;
  if (c.status === 'cancelled') return { state: 'cancelled', daysLeft };
  if (c.result === 'failed') return { state: 'failed', daysLeft };
  if (c.result === 'passed') return { state: c.hasNextContract ? 'hired' : 'passed', daysLeft };
  if (c.status === 'terminated') return { state: 'terminated', daysLeft };
  if (c.effectiveFrom > today) return { state: 'upcoming', daysLeft };
  if (daysLeft !== null && daysLeft < 0) return { state: 'awaiting', daysLeft };
  if (daysLeft !== null && daysLeft <= warnDays) return { state: 'ending', daysLeft };
  return { state: 'running', daysLeft };
}

// ---------------------------------------------------------------------------
// Nội quy, quy định
// ---------------------------------------------------------------------------
// P24 (DEC-459): thêm loại tài liệu công ty – khu, mảng, mã tài liệu ở documents.ts
// DEC-486: thêm safety (tài liệu an toàn: ATLĐ, an toàn điện, PCCC nội bộ, làm việc trên cao, cẩu hàng…)
export const POLICY_CATEGORIES = ['rule', 'regulation', 'guide', 'procedure', 'operation', 'form', 'technical', 'drawing', 'safety', 'legal', 'company_legal'] as const;
export type PolicyCategory = (typeof POLICY_CATEGORIES)[number];
export const POLICY_CATEGORY_LABELS: Record<PolicyCategory, string> = {
  rule: 'Nội quy',
  regulation: 'Quy định',
  guide: 'Hướng dẫn',
  procedure: 'Quy trình',
  operation: 'Hướng dẫn vận hành',
  form: 'Biểu mẫu',
  technical: 'Tài liệu kỹ thuật',
  drawing: 'Bản vẽ mẫu',
  safety: 'Tài liệu an toàn',
  legal: 'Văn bản pháp luật',
  company_legal: 'Hồ sơ pháp lý công ty',
};

/** Phạm vi: toàn công ty / phòng ban / nhóm lao động (employees.work_group – DEC-198) / từng NV */
export const POLICY_SCOPES = ['company', 'department', 'work_group', 'employee'] as const;
export type PolicyScope = (typeof POLICY_SCOPES)[number];
export const POLICY_SCOPE_LABELS: Record<PolicyScope, string> = {
  company: 'Toàn công ty',
  department: 'Phòng ban',
  work_group: 'Nhóm lao động',
  employee: 'Từng nhân viên',
};

/** Văn bản áp cho NV không (theo phạm vi). */
export function policyAppliesTo(
  p: { scopeType: PolicyScope; scopeIds: readonly number[]; scopeGroups: readonly string[] },
  e: { id: number; departmentId: number | null; workGroup: string },
): boolean {
  switch (p.scopeType) {
    case 'company':
      return true;
    case 'department':
      return e.departmentId !== null && p.scopeIds.includes(e.departmentId);
    case 'work_group':
      return p.scopeGroups.includes(e.workGroup);
    case 'employee':
      return p.scopeIds.includes(e.id);
  }
}

/** Ngày lễ (DEC-247): có lương → ô "L" tính 1 công; trừ phép năm → mọi NV nhận đơn phép năm đã duyệt (thiếu phép → không lương). */
export const HOLIDAY_KINDS = ['paid', 'annual_leave'] as const;
export type HolidayKind = (typeof HOLIDAY_KINDS)[number];
export const HOLIDAY_KIND_LABELS: Record<HolidayKind, string> = { paid: 'Lễ có lương', annual_leave: 'Lễ trừ phép năm' };
/** Công ty cho nghỉ hàng loạt (DEC-247) */
export const COMPANY_LEAVE_PAY_KINDS = ['annual', 'paid', 'unpaid'] as const;
export type CompanyLeavePayKind = (typeof COMPANY_LEAVE_PAY_KINDS)[number];
export const COMPANY_LEAVE_PAY_LABELS: Record<CompanyLeavePayKind, string> = { annual: 'Trừ phép năm', paid: 'Có lương', unpaid: 'Không lương' };
